import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { GlobalConstants } from 'src/app/common/global';
import { ModalNames } from 'src/app/common/modal.config';
import { ProfileSidebarComponent } from 'src/app/layout/profile-sidebar/profile-sidebar.component';
import { ErrorMessageService } from 'src/app/services/error-message.util';
import { FormDataStoreService } from 'src/app/services/form-data.store';
import { mustMatch } from 'src/app/utils/validators';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-change-password',
  templateUrl:  `./${environment.pageFolder}/change-password.${environment.pageFolder}.component.html`,
  styleUrls:  [`./${environment.pageFolder}/change-password.${environment.pageFolder}.component.sass`],
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule, ProfileSidebarComponent, CommonModule]
})
export class ChangePasswordComponent implements OnInit {
  errorMessage: string = "";
  successMessage: string = "";

  private readonly formDataKey = `${ModalNames.ChangePassword}`;
  changePasswordForm = this.formBuilder.group({
    oldPassword: ['', [ Validators.required ]],
    newPassword: ['', [ Validators.required ]],
    newPasswordRepeated: ['', [ Validators.required ]],
  }, {
    validators: [ mustMatch('newPassword', 'newPasswordRepeated', 'passwordsDontMatch') ]
  });

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly http: HttpClient,
    private readonly errorMessageService: ErrorMessageService,
     private readonly formDataStore: FormDataStoreService
  ) { }

  ngOnInit(): void {
    const loader = document.getElementById("loader-container")
    loader!.style.display = "none";

    this.changePasswordForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.errorMessage || this.successMessage) {
        this.resetMessages();
      }
    });

    if(environment.configId === "4") {
      this.formDataStore.subscribeToForm(this.formDataKey, this.changePasswordForm, this.destroyRef);
    }
  }

  changePassword() {
    this.resetMessages();
    const controls = this.changePasswordForm.controls;

    if (this.changePasswordForm.invalid) {
      this.changePasswordForm.markAllAsTouched();
      const missingRequired = Object.values(controls).some(control => control.hasError('required'));

      if (missingRequired) {
        this.errorMessage = this.errorMessageService.getErrorMessage('allFieldsRequired');
      } else if (this.changePasswordForm.hasError('passwordsDontMatch')) {
        this.errorMessage = this.errorMessageService.getErrorMessage('passwordFieldsMustMatch');
      } else {
        this.errorMessage = this.errorMessageService.getErrorMessage("error");
      }

      return;
    }
   
    const oldPass = controls['oldPassword'].value;
    const newPass = controls['newPassword'].value;

    this.http.patch<{ success: boolean }>(`${environment.apiUrl}/v1/main/user/password`, {"oldPassword": oldPass, "newPassword": newPass }, { headers: GlobalConstants.headers.headers })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (response) => {
          if(!response.success) {
            this.errorMessage = this.errorMessageService.getErrorMessage('oldPasswordIncorrect');
          } else {
            this.successMessage = this.errorMessageService.getSuccessMessage('oldPasswordChangedSuccess');
          }

          this.formDataStore.clearFormData(this.formDataKey);
        }, error: () => {
          this.errorMessage = this.errorMessageService.getErrorMessage("error");
        }
      })
  }

  resetMessages(): void {
    this.errorMessage = '';
    this.successMessage = '';
  }
}
